"use no memo";

import { useEffect, useState } from "react";
import {
  DeviceConnectionTypes,
  PrinterImageMode,
  PrinterLanguage,
} from "@repo/database";
import { PrinterSchema } from "@repo/validators";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { Printer } from "~/providers/printing-provider";
import { usePlatform } from "~/hooks/use-platform";
import { usePrinterManager } from "~/managers/use-printer-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";
import { Switch } from "../ui/switch";

interface SystemPrinter {
  value: string;
  label: string;
}

export default function PrinterForm({
  printer,
  onClose,
}: {
  printer?: Printer;
  onClose: (force?: boolean) => void;
}) {
  const {
    createPrinter,
    updatePrinter,
    getSystemPrinters,
    testPrinter,
    testDrawer,
  } = usePrinterManager();

  const [systemPrinters, setSystemPrinters] = useState<SystemPrinter[]>([]);

  const { setUnsavedChanges } = useUnsavedChanges();

  const { t, i18n } = useTranslation("common");

  const { platform } = usePlatform();

  const form = useForm({
    reValidateMode: "onChange",
    schema: PrinterSchema(i18n),
    defaultValues: {
      name: printer?.name ?? "",
      connectionType: printer?.connectionType ?? DeviceConnectionTypes.network,
      ipAddress: printer?.ipAddress ?? "",
      port: printer?.port ?? undefined,
      imageMode: printer?.imageMode ?? PrinterImageMode.raster,
      language: printer?.language ?? undefined,
      hasCashDrawer: printer?.hasCashDrawer ?? false,
      width: printer?.width ?? 48,
      copies: printer?.copies ?? 1,
      systemName: printer?.systemName ?? "",
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handleTest = form.handleSubmit((formData) => {
    testPrinter(formData);
  });

  const handleTestDrawer = form.handleSubmit((formData) => {
    testDrawer(formData);
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (printer) {
      const { data, error } = await updatePrinter({
        id: printer.id,
        ...formData,
      });
      if (data) {
        form.reset({}, { keepValues: true });
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    } else {
      const { data, error } = await createPrinter(formData);
      if (data) {
        form.reset({}, { keepValues: true });
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    }
  });

  const connectionType = useWatch({
    control: form.control,
    name: "connectionType",
  });

  const hasCashDrawer = useWatch({
    control: form.control,
    name: "hasCashDrawer",
  });

  useEffect(() => {
    if (connectionType === DeviceConnectionTypes.usb) {
      getSystemPrinters()
        .then((printers) => {
          setSystemPrinters(
            printers.map((printer) => ({
              value: printer.name,
              label: printer.displayName,
            })),
          );
        })
        .catch((e) => console.error(e));
    }
  }, [connectionType, getSystemPrinters]);

  return (
    <Form {...form}>
      <form
        id="printer-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Printer.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t("Printer.name")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="width"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Printer.width")}</FormLabel>
              <FormControl>
                <ModalInput
                  {...field}
                  type="number"
                  label={t("Printer.width")}
                  min={0}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="copies"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Printer.copies")}</FormLabel>
              <FormControl>
                <ModalInput
                  {...field}
                  type="number"
                  label={t("Printer.copies")}
                  min={0}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="language"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Printer.language")}</FormLabel>
              <FormControl>
                <MultiSelect
                  disabled={
                    connectionType === DeviceConnectionTypes.usb &&
                    platform !== "DESKTOP"
                  }
                  maxSelect={1}
                  placeholder="Select printer language..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                    void form.trigger();
                  }}
                  options={Object.values(PrinterLanguage).map((type) => ({
                    label: type,
                    value: type,
                  }))}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="imageMode"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Printer.image_mode")}</FormLabel>
              <FormControl>
                <MultiSelect
                  disabled={
                    connectionType === DeviceConnectionTypes.usb &&
                    platform !== "DESKTOP"
                  }
                  maxSelect={1}
                  placeholder="Select image mode..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                    void form.trigger();
                  }}
                  options={Object.values(PrinterImageMode).map((mode) => ({
                    label: mode,
                    value: mode,
                  }))}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="connectionType"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Printer.connection_type")}</FormLabel>
              <FormControl>
                <MultiSelect
                  disabled={
                    connectionType === DeviceConnectionTypes.usb &&
                    platform !== "DESKTOP"
                  }
                  maxSelect={1}
                  placeholder="Select connection type..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                    void form.trigger();
                  }}
                  options={Object.values(DeviceConnectionTypes).map((type) => ({
                    label: t(`DeviceConnectionTypes.${type}`),
                    value: type,
                    disabled:
                      platform !== "DESKTOP" &&
                      type === DeviceConnectionTypes.usb,
                  }))}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {connectionType === DeviceConnectionTypes.usb && (
          <>
            {platform !== "DESKTOP" && (
              <FormItem className="col-span-full">
                <FormLabel>{t("Printer.system_name")}</FormLabel>
                <Input disabled={true} value={form.getValues("systemName")} />
                <FormDescription>
                  {t("Printer.system_printer_web")}
                </FormDescription>
              </FormItem>
            )}
            {platform === "DESKTOP" && (
              <FormField
                control={form.control}
                name="systemName"
                render={({ field }) => (
                  <FormItem className="col-span-full">
                    <FormLabel>{t("Printer.system_name")}</FormLabel>
                    <FormControl>
                      <MultiSelect
                        maxSelect={1}
                        placeholder="Select system printer..."
                        onValueChange={(value) => {
                          field.onChange(value[0]);
                          void form.trigger();
                        }}
                        options={systemPrinters.map((systemPrinter) => ({
                          label: systemPrinter.label,
                          value: systemPrinter.value,
                        }))}
                        defaultValue={field.value ? [field.value] : []}
                        value={field.value ? [field.value] : []}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </>
        )}
        {connectionType === DeviceConnectionTypes.network && (
          <>
            <FormField
              control={form.control}
              name="ipAddress"
              render={({ field }) => (
                <FormItem className="col-span-full">
                  <FormLabel>{t("Printer.ip_address")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t("Printer.ip_address")} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="port"
              render={({ field }) => (
                <FormItem className="col-span-full">
                  <FormLabel>{t("Printer.port")}</FormLabel>
                  <FormControl>
                    <ModalInput
                      {...field}
                      type="number"
                      label={t("Printer.port")}
                      min={0}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
        <FormField
          control={form.control}
          name="hasCashDrawer"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <Card className="w-full">
                <CardContent className="flex flex-row justify-between items-center">
                  <FormLabel size="lg">{t("Printer.cash_drawer")}</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </CardContent>
              </Card>
            </FormItem>
          )}
        />
        <Button
          onClick={handleTest}
          className="col-span-full"
          variant="success"
        >
          Test printer
        </Button>
        {hasCashDrawer === true && (
          <Button
            onClick={handleTestDrawer}
            className="col-span-full"
            variant="warning"
          >
            Test drawer
          </Button>
        )}
        {form.formState.errors.root && (
          <FormMessage className="col-span-full">
            {form.formState.errors.root.message}
          </FormMessage>
        )}
      </form>
    </Form>
  );
}
